import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, Link, useNavigate } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Toaster } from 'sonner';
import {
  Command as CmdIcon,
  Network,
  ChevronLeft,
  ChevronDown,
  Shuffle,
  Home,
  FolderOpenDot,
  ListTodo,
  Map as MapIcon,
  FileText,
  Sparkles,
  Coins,
  Database,
  Settings,
  Building2,
  Users,
  Mail,
  ShieldCheck,
  Check,
  BarChart3,
  Lock,
} from 'lucide-react';
import {
  useAuth,
  useIsOrgAdmin,
  useIsSuperAdmin,
} from '@/components/providers/auth-provider';
import { useUiStore } from '@/stores/ui-store';
import { useCommandPaletteStore } from '@/stores/command-palette-store';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Logo } from './logo';
import { ProfileMenu } from './profile-menu';
import { CommandPalette } from './command-palette';
import { TaskTray } from './bespoke/task-tray';
import {
  SplitPane,
  splitPaneGridStyle,
  type SplitPaneSection,
} from './bespoke/split-pane';
import { StatusPaneSection } from './bespoke/status-pane-section';
import { StatsPaneSection } from './bespoke/stats-pane-section';
import {
  PANE_SECTION,
  PagePaneSectionsProvider,
  usePagePaneSectionsValue,
} from './page-pane-sections-context';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useDemoMode } from '@/lib/demo-mode';
import type { LucideIcon } from 'lucide-react';
import type { StringKey } from '@/lib/i18n';

interface NavItem {
  to: string;
  labelKey: StringKey;
  Icon: LucideIcon;
}

const primaryNavItems: NavItem[] = [
  { to: '/', labelKey: 'nav.command-center', Icon: Home },
  { to: '/query', labelKey: 'nav.query', Icon: Sparkles },
  { to: '/graph', labelKey: 'nav.graph', Icon: Network },
  { to: '/map', labelKey: 'nav.map', Icon: MapIcon },
  { to: '/brief', labelKey: 'nav.briefs', Icon: FileText },
];

const secondaryNavItems: NavItem[] = [
  { to: '/capital', labelKey: 'nav.capital', Icon: Coins },
  { to: '/investigations', labelKey: 'nav.investigations', Icon: FolderOpenDot },
  { to: '/todos', labelKey: 'nav.todos', Icon: ListTodo },
  { to: '/er', labelKey: 'nav.er', Icon: Shuffle },
  { to: '/sources', labelKey: 'nav.sources', Icon: Database },
];

// Org-admin nav tail. Gated on render by `useIsOrgAdmin()` so a plain
// member never sees the entry; the route `beforeLoad` also enforces the
// role, so this is strictly a visibility affordance.
const adminNavItems: NavItem[] = [
  { to: '/settings/org', labelKey: 'org.settings', Icon: Building2 },
  { to: '/settings/org/members', labelKey: 'org.members', Icon: Users },
  { to: '/settings/org/invites', labelKey: 'org.invites', Icon: Mail },
  { to: '/settings/org/ingest', labelKey: 'org.ingest.nav', Icon: Database },
];

// Platform super-admin nav tail. `/admin/settings` (graph grouping, ingest,
// Neo4j health) is cross-tenant so it lives here, not under org-admin.
const superAdminNavItems: NavItem[] = [
  { to: '/superadmin', labelKey: 'superadmin.section', Icon: ShieldCheck },
  { to: '/admin/settings', labelKey: 'menu.admin-settings', Icon: Settings },
];

export function AppShell() {
  return (
    <PagePaneSectionsProvider>
      <AppShellInner />
      <AppToaster />
    </PagePaneSectionsProvider>
  );
}

/**
 * Themed sonner mount. Theme + dir come from the UI store so toasts
 * flip with the language switcher (RTL slide-from-end on `fa`) and
 * the theme toggle (daylight/midnight). `richColors` opts into
 * sonner's per-variant accent palette which we re-skin via the
 * `[--toast-bg]`/`[--toast-text]`/`[--toast-border]` CSS-var contract
 * so success/error/warning land on our `--color-*` tokens instead
 * of sonner's defaults.
 */
function AppToaster() {
  const lang = useUiStore((s) => s.lang);
  const theme = useUiStore((s) => s.theme);
  // The store's theme is one of 'daylight' | 'midnight' | 'system'.
  // sonner uses 'light' | 'dark' | 'system'. The `'system'` fall-
  // through matters: a user on `theme: 'system'` with dark-mode at
  // the OS level would otherwise get a light-themed toast against a
  // dark page.
  const sonnerTheme: 'light' | 'dark' | 'system' =
    theme === 'midnight' ? 'dark' : theme === 'system' ? 'system' : 'light';
  return (
    <Toaster
      position={lang === 'fa' ? 'bottom-left' : 'bottom-right'}
      theme={sonnerTheme}
      richColors
      closeButton
      toastOptions={{
        classNames: {
          toast:
            'group toast border-[var(--color-border)] bg-[var(--color-elevated)] text-[var(--color-ink)] font-sans text-sm shadow-lg',
          description: 'text-xs text-[var(--color-ink-muted)]',
          actionButton:
            'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-bright)]',
          success:
            'border-[var(--color-success-soft)] text-[var(--color-success)]',
          error:
            'border-[var(--color-critical-soft)] text-[var(--color-critical)]',
          warning:
            'border-[var(--color-warning-soft)] text-[var(--color-warning)]',
        },
      }}
    />
  );
}

function AppShellInner() {
  const { user, memberships, activeOrg, switchOrg, loading } = useAuth();
  const navigate = useNavigate();
  const {
    lang,
    sidebarCollapsed,
    toggleSidebar,
  } = useUiStore();
  const openPalette = useCommandPaletteStore((s) => s.setOpen);
  const demoMode = useDemoMode();
  const isOrgAdmin = useIsOrgAdmin();
  const isSuperAdmin = useIsSuperAdmin();

  // ── Hover-to-peek the collapsed sidebar ────────────────────────────
  // When the analyst has the rail collapsed, hovering it temporarily
  // expands without flipping the persisted preference.
  //
  // Asymmetric delays (research-backed — NN/G, Baymard, hoverIntent):
  //   - 150 ms ENTER delay filters cursor flybys without feeling laggy
  //     on an intentional park. 0 ms triggers on every grazing motion.
  //   - 450 ms LEAVE delay forgives diagonal-to-target overshoots
  //     (user moving toward a nav item, briefly clipping outside the
  //     rail). NN/G's "keep open >500 ms after exit" floor for hover-
  //     revealed content; Baymard's 300–500 ms anti-flicker range.
  const HOVER_ENTER_DELAY_MS = 150;
  const HOVER_LEAVE_DELAY_MS = 450;

  const [hoverExpanded, setHoverExpanded] = useState(false);
  const enterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelEnter = useCallback(() => {
    if (enterTimerRef.current !== null) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
  }, []);
  const cancelLeave = useCallback(() => {
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);
  const onSidebarMouseEnter = useCallback(() => {
    cancelLeave();
    if (!sidebarCollapsed) return;
    cancelEnter();
    enterTimerRef.current = setTimeout(() => {
      setHoverExpanded(true);
      enterTimerRef.current = null;
    }, HOVER_ENTER_DELAY_MS);
  }, [cancelEnter, cancelLeave, sidebarCollapsed]);
  const onSidebarMouseLeave = useCallback(() => {
    // Cancel a pending expand if the user left before the enter delay
    // fired (a flyby) — never let an expand land after the cursor is gone.
    cancelEnter();
    cancelLeave();
    leaveTimerRef.current = setTimeout(() => {
      setHoverExpanded(false);
      leaveTimerRef.current = null;
    }, HOVER_LEAVE_DELAY_MS);
  }, [cancelEnter, cancelLeave]);
  // If the user toggles the sidebar to permanently expanded while
  // hovering, drop the peek state so the persisted value is the source
  // of truth and the leave-timer can't accidentally collapse it.
  useEffect(() => {
    if (!sidebarCollapsed && hoverExpanded) setHoverExpanded(false);
  }, [sidebarCollapsed, hoverExpanded]);
  useEffect(
    () => () => {
      cancelEnter();
      cancelLeave();
    },
    [cancelEnter, cancelLeave],
  );
  const effectivelyCollapsed = sidebarCollapsed && !hoverExpanded;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-[var(--color-ink-muted)]">
        {t('common.loading', lang)}
      </div>
    );
  }
  if (!user) {
    navigate({ to: '/sign-in', replace: true });
    return null;
  }

  return (
    <div className="relative flex min-h-screen text-[var(--color-ink)]">
      <aside
        className={cn(
          'glass-nav hidden shrink-0 border-e transition-[width] duration-280',
          // Sticky + h-screen keeps the status panel pinned to the
          // viewport bottom on long pages; nav scrolls internally if its
          // own content overflows the available space.
          'md:sticky md:top-0 md:flex md:h-screen md:flex-col md:overflow-hidden',
          effectivelyCollapsed ? 'w-14 p-2' : 'w-56 p-4',
        )}
        onMouseEnter={onSidebarMouseEnter}
        onMouseLeave={onSidebarMouseLeave}
        aria-label="Primary navigation"
      >
        <Link
          to="/"
          className={cn(
            'mb-6 flex items-center gap-2 whitespace-nowrap font-serif text-[var(--color-ink)]',
            effectivelyCollapsed ? 'justify-center' : 'items-baseline',
          )}
          aria-label={t('app.name', lang)}
          title={t('app.name', lang)}
        >
          <Logo
            size={effectivelyCollapsed ? 28 : 24}
            ring={effectivelyCollapsed}
            className="text-[var(--color-ink)] self-center"
          />
          {!effectivelyCollapsed && (
            <>
              <span className="text-xl">{t('app.name', lang)}</span>
              <span className="text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
                جامِ جم
              </span>
            </>
          )}
        </Link>

        <nav
          aria-label="Primary"
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto text-sm"
        >
          {primaryNavItems.map((item) => (
            <SideLink
              key={item.to}
              to={item.to}
              label={t(item.labelKey, lang)}
              icon={<item.Icon className="size-4" />}
              collapsed={effectivelyCollapsed}
            />
          ))}

          {demoMode ? (
            <details className="group mt-1">
              <summary
                className={cn(
                  'flex cursor-pointer list-none items-center gap-2 whitespace-nowrap rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink-muted)] transition-colors duration-150 hover:bg-[var(--color-accent-softer)] hover:text-[var(--color-ink)]',
                  effectivelyCollapsed && 'justify-center',
                )}
                title={effectivelyCollapsed ? t('nav.more', lang) : undefined}
                aria-label={t('nav.more', lang)}
              >
                <ChevronLeft
                  className="size-4 icon-directional -rotate-90 transition-transform duration-150 group-open:rotate-90"
                  aria-hidden
                />
                {!effectivelyCollapsed && <span>{t('nav.more', lang)}</span>}
              </summary>
              <div className="mt-1 flex flex-col gap-1">
                {secondaryNavItems.map((item) => (
                  <SideLink
                    key={item.to}
                    to={item.to}
                    label={t(item.labelKey, lang)}
                    icon={<item.Icon className="size-4" />}
                    collapsed={effectivelyCollapsed}
                  />
                ))}
              </div>
            </details>
          ) : (
            secondaryNavItems.map((item) => (
              <SideLink
                key={item.to}
                to={item.to}
                label={t(item.labelKey, lang)}
                icon={<item.Icon className="size-4" />}
                collapsed={effectivelyCollapsed}
              />
            ))
          )}

          {isOrgAdmin && (
            <>
              <div
                className={cn(
                  'mt-3 whitespace-nowrap border-t border-[var(--color-border)] pt-2 font-mono text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]',
                  effectivelyCollapsed && 'hidden',
                )}
              >
                admin
              </div>
              {adminNavItems.map((item) => (
                <SideLink
                  key={item.to}
                  to={item.to}
                  label={t(item.labelKey, lang)}
                  icon={<item.Icon className="size-4" />}
                  collapsed={effectivelyCollapsed}
                />
              ))}
            </>
          )}
          {isSuperAdmin && (
            <>
              <div
                className={cn(
                  'mt-3 whitespace-nowrap border-t border-[var(--color-border)] pt-2 font-mono text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]',
                  effectivelyCollapsed && 'hidden',
                )}
              >
                platform
              </div>
              {superAdminNavItems.map((item) => (
                <SideLink
                  key={item.to}
                  to={item.to}
                  label={t(item.labelKey, lang)}
                  icon={<item.Icon className="size-4" />}
                  collapsed={effectivelyCollapsed}
                />
              ))}
            </>
          )}
        </nav>

        <div className="mt-auto space-y-2">
          {/* Classification + session + email moved into the AppShell right rail
              as the always-present "Status" tab — see <StatusPaneSection />. */}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex w-full items-center justify-center rounded-xs border border-[var(--color-border)] py-1 text-xxs text-[var(--color-ink-muted)] hover:bg-[var(--color-accent-softer)] hover:text-[var(--color-ink)]',
            )}
          >
            <ChevronLeft
              className={cn(
                'size-3.5 icon-directional transition-transform duration-280',
                sidebarCollapsed && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
        </div>
      </aside>

      <main className="relative flex min-h-screen flex-1 flex-col">
        <header className="glass-nav sticky top-0 z-20 grid h-14 grid-cols-[1fr_minmax(0,640px)_1fr] items-center gap-3 border-b px-4">
          <div className="flex items-center">
            {memberships.length > 1 && activeOrg && (
              <OrgSwitcher
                activeId={activeOrg.id}
                activeName={activeOrg.name}
                memberships={memberships}
                onSwitch={(id) => void switchOrg(id)}
                ariaLabel={t('org.switch', lang)}
              />
            )}
            {memberships.length <= 1 && activeOrg && (
              <div
                className="hidden truncate font-mono text-xxs text-[var(--color-ink-muted)] md:inline-block"
                title={activeOrg.name}
              >
                {activeOrg.name}
              </div>
            )}
          </div>

          <Button
            variant="outline"
            onClick={() => openPalette(true)}
            className="w-full justify-start gap-2 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
            aria-label="Open command palette"
          >
            <CmdIcon className="size-3.5" />
            <span className="hidden sm:inline">
              {lang === 'fa' ? 'جست‌وجو / دستور' : 'Search or ask'}
            </span>
            <kbd className="ms-auto rounded-xs border border-[var(--color-border)] px-1 font-mono text-xxs">
              ⌘K
            </kbd>
          </Button>

          <div className="flex items-center justify-self-end gap-1">
            <ProfileMenu />
          </div>

          <div className="nav-scan" aria-hidden="true" />
        </header>

        <RightRailLayout>
          <Outlet />
        </RightRailLayout>
      </main>

      <CommandPalette />
      <TaskTray />
    </div>
  );
}

/**
 * Composition order in the rail: [...page-registered, stats, status]. When
 * the persisted active section id isn't present on the current page,
 * <SplitPane> falls back to the first available — so leaving the rail on
 * "Threads" in command center doesn't trap the user on a page without it.
 */
function RightRailLayout({ children }: { children: ReactNode }) {
  const lang = useUiStore((s) => s.lang);
  const splitPaneOpen = useUiStore((s) => s.splitPaneOpen);
  const splitPaneWidth = useUiStore((s) => s.splitPaneWidth);
  const splitPaneActiveSectionId = useUiStore(
    (s) => s.splitPaneActiveSectionId,
  );
  const toggleSplitPane = useUiStore((s) => s.toggleSplitPane);
  const setSplitPaneWidth = useUiStore((s) => s.setSplitPaneWidth);
  const setSplitPaneActiveSectionId = useUiStore(
    (s) => s.setSplitPaneActiveSectionId,
  );
  const pageSections = usePagePaneSectionsValue();
  const gridRef = useRef<HTMLDivElement>(null);

  const sections = useMemo<SplitPaneSection[]>(() => {
    const statsOverride = pageSections.find((s) => s.id === PANE_SECTION.stats);
    const pageContent = pageSections.filter(
      (s) => s.id !== PANE_SECTION.stats && s.id !== PANE_SECTION.status,
    );
    const statsSection: SplitPaneSection = statsOverride ?? {
      id: PANE_SECTION.stats,
      title: t('pane.stats.title', lang),
      icon: <BarChart3 className="size-3.5" />,
      content: (
        <ScrollArea className="h-full">
          <StatsPaneSection />
        </ScrollArea>
      ),
    };
    const statusSection: SplitPaneSection = {
      id: PANE_SECTION.status,
      title: t('pane.status.title', lang),
      icon: <Lock className="size-3.5" />,
      content: (
        <ScrollArea className="h-full">
          <StatusPaneSection />
        </ScrollArea>
      ),
    };
    return [...pageContent, statsSection, statusSection];
  }, [lang, pageSections]);

  const gridStyle = splitPaneGridStyle(splitPaneOpen, splitPaneWidth);

  // `relative flex-1 overflow-hidden` height-bounds the pages so internal
  // scrollers (ScrollArea, GraphCanvas) work and the rail stays pinned to
  // the viewport instead of growing with body scroll.
  return (
    <div
      ref={gridRef}
      className="relative grid flex-1 grid-cols-1 grid-rows-[1fr] overflow-hidden lg:grid-cols-[1fr_var(--split-pane-w)]"
      style={gridStyle}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
      <SplitPane
        open={splitPaneOpen}
        onToggle={toggleSplitPane}
        sections={sections}
        activeSectionId={splitPaneActiveSectionId}
        onActiveSectionChange={setSplitPaneActiveSectionId}
        width={splitPaneWidth}
        onCommitWidth={setSplitPaneWidth}
        gridRef={gridRef}
      />
    </div>
  );
}

function OrgSwitcher({
  activeId,
  activeName,
  memberships,
  onSwitch,
  ariaLabel,
}: {
  activeId: string;
  activeName: string;
  memberships: ReadonlyArray<{ id: string; name: string; slug: string }>;
  onSwitch: (id: string) => void;
  ariaLabel: string;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          title={activeName}
          className={cn(
            'inline-flex max-w-[220px] items-center gap-1.5 rounded-sm border border-[var(--color-border)] bg-[var(--color-elevated)] px-2.5 py-1.5 text-xs text-[var(--color-ink)] transition-colors hover:border-[var(--color-accent)]',
            'data-[state=open]:border-[var(--color-accent)]',
          )}
        >
          <Building2 className="size-3.5 text-[var(--color-ink-muted)]" aria-hidden />
          <span className="truncate">{activeName}</span>
          <ChevronDown
            className="size-3.5 text-[var(--color-ink-muted)]"
            aria-hidden
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="start"
          className="z-50 min-w-[220px] rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-sm shadow-md"
        >
          {memberships.map((m) => {
            const isActive = m.id === activeId;
            return (
              <DropdownMenu.Item
                key={m.id}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!isActive) onSwitch(m.id);
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm outline-none',
                  'data-[highlighted]:bg-[var(--color-elevated)]',
                  isActive && 'bg-[var(--color-accent-soft)]',
                )}
              >
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {isActive && (
                    <Check
                      className="size-3.5 text-[var(--color-accent)]"
                      aria-hidden
                    />
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[var(--color-ink)]">
                    {m.name}
                  </span>
                  <span className="truncate font-mono text-xxs text-[var(--color-ink-muted)]">
                    {m.slug}
                  </span>
                </span>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function SideLink({
  to,
  label,
  icon,
  collapsed,
}: {
  to: string;
  label: string;
  icon?: ReactNode;
  collapsed?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cn(
        // `whitespace-nowrap` keeps the label from wrapping to a second
        // line during the width animation when the rail expands —
        // the parent aside has `overflow-hidden`, so an overflowing
        // label is simply clipped instead of reflowing.
        'group flex items-center gap-2 whitespace-nowrap rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink-secondary)] transition-colors duration-150 hover:bg-[var(--color-accent-softer)] hover:text-[var(--color-ink)]',
        collapsed && 'justify-center',
      )}
      activeProps={{
        className:
          'bg-[var(--color-accent-soft)] text-[var(--color-ink)] shadow-[inset_0_0_0_1px_var(--color-accent-softer)] pulse-ring',
      }}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
    >
      {icon}
      {!collapsed && <span>{label}</span>}
    </Link>
  );
}
